import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache for module status to avoid repeated API calls
let moduleCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to check module access based on user role and module settings
 * 
 * For regular users: checks status_user
 * For admin users (in their user dashboard): checks status_admin
 * 
 * This does NOT affect the admin dashboard - only user-facing features
 */
export const useModuleAccess = () => {
  const { user } = useAuth();
  const [modules, setModules] = useState(moduleCache || {});
  const [loading, setLoading] = useState(!moduleCache);
  const [error, setError] = useState(null);

  const fetchModules = useCallback(async (force = false) => {
    // Use cache if available and not expired
    if (!force && moduleCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      setModules(moduleCache);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin-tools/modules/public`);
      const moduleData = response.data.modules || {};
      
      // Update cache
      moduleCache = moduleData;
      cacheTimestamp = Date.now();
      
      setModules(moduleData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch module status:', err);
      setError(err);
      // On error, default to allowing access (fail open for better UX)
      // In production, you might want to fail closed for security
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  /**
   * Check if a specific module is active for the current user
   * @param {string} moduleKey - The module key to check (e.g., 'meo_class_2_oral')
   * @returns {boolean} - Whether the module is active
   */
  const isModuleActive = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      // Module not found - default to active (fail open)
      return true;
    }

    const module = modules[moduleKey];
    
    // Check based on user role
    // For admins viewing their user dashboard, check status_admin
    // For regular users, check status_user
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    const status = isAdmin ? module.status_admin : module.status_user;
    
    return status === 'active';
  }, [modules, user?.role]);

  /**
   * Check if a module shows "Coming Soon" for the current user
   * @param {string} moduleKey - The module key to check
   * @returns {boolean} - Whether the module is coming soon
   */
  const isModuleComingSoon = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      return false;
    }

    const module = modules[moduleKey];
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    const status = isAdmin ? module.status_admin : module.status_user;
    
    return status === 'coming_soon';
  }, [modules, user?.role]);

  /**
   * Check if a module is hidden for regular users (not visible at all)
   * @param {string} moduleKey - The module key to check
   * @returns {boolean} - Whether the module is hidden
   */
  const isModuleHidden = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      return false;
    }

    const module = modules[moduleKey];
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    
    // Admins can always see hidden modules
    if (isAdmin) return false;
    
    return module.status_user === 'hidden';
  }, [modules, user?.role]);

  /**
   * Check if a module is visible based on visibility setting
   * Handles guest visibility (everyone vs logged_in vs hidden)
   * For child modules, visibility is inherited from parent module
   * @param {string} moduleKey - The module key to check
   * @returns {boolean} - Whether the module should be shown
   */
  const isModuleVisible = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      // Module not found - default to visible
      return true;
    }

    const module = modules[moduleKey];
    // Visibility is already resolved at parent level by the API
    // (child modules inherit visibility from parent)
    const visibility = module.visibility || 'logged_in';
    const isLoggedIn = !!user;
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    
    // Admins can always see all modules
    if (isAdmin) return true;
    
    // Check visibility setting
    switch (visibility) {
      case 'everyone':
        return true; // Visible to guests and logged-in users
      case 'logged_in':
        return isLoggedIn; // Only visible if logged in
      case 'hidden':
        return false; // Hidden from everyone (except admins, handled above)
      default:
        return isLoggedIn; // Default to logged_in behavior
    }
  }, [modules, user]);

  /**
   * Get the visibility setting of a module
   * @param {string} moduleKey - The module key to check
   * @returns {'everyone' | 'logged_in' | 'hidden' | null}
   */
  const getModuleVisibility = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      return null;
    }
    return modules[moduleKey].visibility || 'logged_in';
  }, [modules]);

  /**
   * Check if a module is completely disabled for the current user
   * @param {string} moduleKey - The module key to check
   * @returns {boolean} - Whether the module is disabled
   */
  const isModuleDisabled = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      return false;
    }

    const module = modules[moduleKey];
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    const status = isAdmin ? module.status_admin : module.status_user;
    
    return status === 'disabled';
  }, [modules, user?.role]);

  /**
   * Get the status of a module for the current user
   * @param {string} moduleKey - The module key to check
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getModuleStatus = useCallback((moduleKey) => {
    if (!modules || !modules[moduleKey]) {
      return null;
    }

    const module = modules[moduleKey];
    const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    return isAdmin ? module.status_admin : module.status_user;
  }, [modules, user?.role]);

  /**
   * Check if a grade is active (checks both oral and written)
   * Returns true if at least one exam type is active
   * @param {string} grade - The grade name (e.g., 'MEO Class 2')
   * @returns {boolean}
   */
  const isGradeActive = useCallback((grade) => {
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const oralKey = `${gradeKey}_oral`;
    const writtenKey = `${gradeKey}_written`;
    
    return isModuleActive(oralKey) || isModuleActive(writtenKey);
  }, [isModuleActive]);

  /**
   * Check if oral exams are active for a grade
   * @param {string} grade - The grade name
   * @returns {boolean}
   */
  const isOralActive = useCallback((grade) => {
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    return isModuleActive(`${gradeKey}_oral`);
  }, [isModuleActive]);

  /**
   * Check if written exams are active for a grade
   * @param {string} grade - The grade name
   * @returns {boolean}
   */
  const isWrittenActive = useCallback((grade) => {
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    return isModuleActive(`${gradeKey}_written`);
  }, [isModuleActive]);

  /**
   * Get status for oral exams of a grade
   * @param {string} grade - The grade name
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getOralStatus = useCallback((grade) => {
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    return getModuleStatus(`${gradeKey}_oral`);
  }, [getModuleStatus]);

  /**
   * Get status for written exams of a grade
   * @param {string} grade - The grade name
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getWrittenStatus = useCallback((grade) => {
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    return getModuleStatus(`${gradeKey}_written`);
  }, [getModuleStatus]);

  /**
   * Filter an array of grades to only include active ones
   * @param {Array} grades - Array of grade objects with 'grade' or 'grade_key' property
   * @returns {Array} - Filtered array of active grades
   */
  const filterActiveGrades = useCallback((grades) => {
    if (!grades || !Array.isArray(grades)) return [];
    
    return grades.filter(g => {
      const gradeName = g.grade || g.grade_key || g.display_name || g;
      return isGradeActive(gradeName);
    });
  }, [isGradeActive]);

  /**
   * Check if a feature module is active (e.g., 'coin_rewards', 'two_factor_auth')
   * @param {string} featureKey - The feature module key
   * @returns {boolean}
   */
  const isFeatureActive = useCallback((featureKey) => {
    return isModuleActive(featureKey);
  }, [isModuleActive]);

  /**
   * Check if Practice Sessions feature is active
   * @returns {boolean}
   */
  const isPracticeSessionsActive = useCallback(() => {
    return isModuleActive('practice_sessions');
  }, [isModuleActive]);

  /**
   * Get Practice Sessions status
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getPracticeSessionsStatus = useCallback(() => {
    return getModuleStatus('practice_sessions');
  }, [getModuleStatus]);

  /**
   * Check if Progress Tracker feature is active
   * @returns {boolean}
   */
  const isProgressTrackerActive = useCallback(() => {
    return isModuleActive('progress_tracker');
  }, [isModuleActive]);

  /**
   * Get Progress Tracker status
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getProgressTrackerStatus = useCallback(() => {
    return getModuleStatus('progress_tracker');
  }, [getModuleStatus]);

  /**
   * Check if Coin Rewards feature is active
   * @returns {boolean}
   */
  const isCoinRewardsActive = useCallback(() => {
    return isModuleActive('coin_rewards');
  }, [isModuleActive]);

  /**
   * Get Coin Rewards status
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getCoinRewardsStatus = useCallback(() => {
    return getModuleStatus('coin_rewards');
  }, [getModuleStatus]);

  /**
   * Check if Study Notebook is active for a specific grade
   * @param {string} grade - The grade name (e.g., 'MEO Class 2')
   * @returns {boolean}
   */
  const isNotebookActiveForGrade = useCallback((grade) => {
    if (!grade) return false;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `notebook_${gradeKey}`;
    return isModuleActive(moduleKey);
  }, [isModuleActive]);

  /**
   * Get Study Notebook status for a specific grade
   * @param {string} grade - The grade name
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getNotebookStatusForGrade = useCallback((grade) => {
    if (!grade) return null;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `notebook_${gradeKey}`;
    return getModuleStatus(moduleKey);
  }, [getModuleStatus]);

  /**
   * Check if Exam Insights is active for a specific grade
   * @param {string} grade - The grade name (e.g., 'MEO Class 2')
   * @returns {boolean}
   */
  const isInsightsActiveForGrade = useCallback((grade) => {
    if (!grade) return false;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `insights_${gradeKey}`;
    return isModuleActive(moduleKey);
  }, [isModuleActive]);

  /**
   * Get Exam Insights status for a specific grade
   * @param {string} grade - The grade name
   * @returns {'active' | 'coming_soon' | 'disabled' | null}
   */
  const getInsightsStatusForGrade = useCallback((grade) => {
    if (!grade) return null;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `insights_${gradeKey}`;
    return getModuleStatus(moduleKey);
  }, [getModuleStatus]);

  /**
   * Check if Live Classes is active for a specific grade. Grade-wise
   * rollout: admin flips each grade from `coming_soon` → `active` as
   * instructor/host capacity comes online. Note: this only reflects the
   * module_settings state; plan-based access (paid vs free) is separate
   * and lives in useSubscriptionAccess.
   * @param {string} grade
   * @returns {boolean}
   */
  const isLiveClassesActiveForGrade = useCallback((grade) => {
    if (!grade) return false;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `live_classes_${gradeKey}`;
    return isModuleActive(moduleKey);
  }, [isModuleActive]);

  /**
   * Get Live Classes status for a specific grade
   * @param {string} grade
   * @returns {'active' | 'coming_soon' | 'hidden' | 'disabled' | null}
   */
  const getLiveClassesStatusForGrade = useCallback((grade) => {
    if (!grade) return null;
    const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
    const moduleKey = `live_classes_${gradeKey}`;
    return getModuleStatus(moduleKey);
  }, [getModuleStatus]);

  /**
   * Check if Forum feature is active
   * @returns {boolean}
   */
  const isForumActive = useCallback(() => {
    return isModuleActive('forum');
  }, [isModuleActive]);

  /**
   * Get Forum status
   * @returns {'active' | 'coming_soon' | 'hidden' | 'disabled' | null}
   */
  const getForumStatus = useCallback(() => {
    return getModuleStatus('forum');
  }, [getModuleStatus]);

  /**
   * Check if Ask (Semantic Search) feature is active
   * @returns {boolean}
   */
  const isAskActive = useCallback(() => {
    return isModuleActive('ask_semantic_search');
  }, [isModuleActive]);

  /**
   * Get Ask (Semantic Search) status
   * @returns {'active' | 'coming_soon' | 'hidden' | 'disabled' | null}
   */
  const getAskStatus = useCallback(() => {
    return getModuleStatus('ask_semantic_search');
  }, [getModuleStatus]);

  // Refresh function to force update cache
  const refresh = useCallback(() => {
    fetchModules(true);
  }, [fetchModules]);

  return {
    loading,
    error,
    modules,
    isModuleActive,
    isModuleComingSoon,
    isModuleHidden,
    isModuleVisible,
    getModuleVisibility,
    isModuleDisabled,
    getModuleStatus,
    isGradeActive,
    isOralActive,
    isWrittenActive,
    getOralStatus,
    getWrittenStatus,
    filterActiveGrades,
    isFeatureActive,
    isPracticeSessionsActive,
    getPracticeSessionsStatus,
    isProgressTrackerActive,
    getProgressTrackerStatus,
    isCoinRewardsActive,
    getCoinRewardsStatus,
    isNotebookActiveForGrade,
    getNotebookStatusForGrade,
    isInsightsActiveForGrade,
    getInsightsStatusForGrade,
    isLiveClassesActiveForGrade,
    getLiveClassesStatusForGrade,
    isForumActive,
    getForumStatus,
    isAskActive,
    getAskStatus,
    refresh
  };
};

/**
 * Grade to module key mapping helper
 */
export const gradeToModuleKey = (grade, examType = 'oral') => {
  const gradeKey = grade.toLowerCase().replace(/\s+/g, '_');
  return `${gradeKey}_${examType}`;
};

/**
 * List of all grade module keys
 */
export const GRADE_MODULE_KEYS = {
  'MEO Class 1': { oral: 'meo_class_1_oral', written: 'meo_class_1_written' },
  'MEO Class 2': { oral: 'meo_class_2_oral', written: 'meo_class_2_written' },
  'MEO Class 4': { oral: 'meo_class_4_oral', written: 'meo_class_4_written' },
  'ETO': { oral: 'eto_oral', written: 'eto_written' },
  'Master': { oral: 'master_oral', written: 'master_written' },
  'Chief Mate': { oral: 'chief_mate_oral', written: 'chief_mate_written' },
  '2MFG': { oral: '2mfg_oral', written: '2mfg_written' }
};

/**
 * List of notebook module keys by grade
 */
export const NOTEBOOK_MODULE_KEYS = {
  'MEO Class 1': 'notebook_meo_class_1',
  'MEO Class 2': 'notebook_meo_class_2',
  'MEO Class 4': 'notebook_meo_class_4',
  'ETO': 'notebook_eto',
  'Master': 'notebook_master',
  'Chief Mate': 'notebook_chief_mate',
  '2MFG': 'notebook_2mfg'
};

/**
 * List of insights module keys by grade
 */
export const INSIGHTS_MODULE_KEYS = {
  'MEO Class 1': 'insights_meo_class_1',
  'MEO Class 2': 'insights_meo_class_2',
  'MEO Class 4': 'insights_meo_class_4',
  'ETO': 'insights_eto',
  'Master': 'insights_master',
  'Chief Mate': 'insights_chief_mate',
  '2MFG': 'insights_2mfg'
};

export default useModuleAccess;
